import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import useDepollution from "../../data/useDepollution";
import useDefaultCRUDHandlers from "../../hooks/useDefaultCRUDHandlers";
import Modal from "../../layouts/Modal";
import { ICreateUpdateDepollutionStepRequest, IDepollutionStep } from "../../models/Depollution";
import ConfirmModal from "../shared/Confirm/ConfirmModal";
import { ButtonInput } from "../shared/Inputs/ButtonInput";
import SwitchInput from "../shared/Inputs/SwitchInput";
import { TextInput } from "../shared/Inputs/TextInput";
import { SystemStepFieldPicker, systemStepOptionsString, useSystemStepField } from "../shared/SystemStepField";

type FormValues = {
  name: string,
  subtitle: string,
  order: number,
  needsValue: boolean,
  options: string,
  isActive: boolean,
};

interface DepollutionStepModalProps {
  open: boolean,
  setOpen: React.Dispatch<React.SetStateAction<boolean>>,
  depollutionStep?: IDepollutionStep;
  defaultOrder: number;
}

export default function DepollutionStepModal({ open, setOpen, depollutionStep, defaultOrder }: DepollutionStepModalProps) {
  const { t } = useTranslation("vehicles");
  const { saveHandlers } = useDefaultCRUDHandlers(t("Depollution Step"));
  const { deleteHandlers } = useDefaultCRUDHandlers(t("Depollution Step"));
  const { createDepollutionStep, updateDepollutionStep, deleteDepollutionStep } = useDepollution();
  const [deleteOpen, setDeleteOpen] = useState<boolean>(false);
  const { register, handleSubmit, reset, setValue, watch } = useForm<FormValues>();
  // Set when this is a system step - its answer is written onto the vehicle/lead when it's ticked off.
  const [systemField, setSystemField] = useState<string | null>(null);
  const { field: systemStepField } = useSystemStepField(systemField);

  const onSubmit = (data: FormValues) => {
    const updatedDepollutionStep: ICreateUpdateDepollutionStepRequest = {
      ...depollutionStep,
      name: data.name,
      subtitle: data.subtitle?.trim() || null,
      order: Number(data.order),
      // A system step's answers are fixed by its field; the API enforces the same.
      needsValue: systemStepField ? true : data.needsValue,
      options: systemStepField ? systemStepOptionsString(systemStepField) : data.options?.trim() || null,
      systemField,
      isActive: data.isActive,
    };

    if (depollutionStep !== undefined) {
      updateDepollutionStep.mutate(updatedDepollutionStep, {
        onSuccess: () => {
          saveHandlers.onSuccess();
          setOpen(false);
          reset({});
        },
        onError: saveHandlers.onError
      });
    } else {
      createDepollutionStep.mutate(updatedDepollutionStep, {
        onSuccess: () => {
          saveHandlers.onSuccess();
          setOpen(false);
          reset({});
        },
        onError: saveHandlers.onError
      });
    }
  };

  const handleDelete = () => {
    deleteDepollutionStep.mutate(depollutionStep?.id!, {
      onSuccess: () => {
        deleteHandlers.onSuccess();
        setOpen(false);
        setDeleteOpen(false);
        reset({});
      },
      onError: deleteHandlers.onError
    });
  };

  useEffect(() => {
    if (depollutionStep !== undefined) {
      reset({
        name: depollutionStep.name,
        subtitle: depollutionStep.subtitle ?? "",
        order: depollutionStep.order,
        needsValue: depollutionStep.needsValue,
        options: depollutionStep.options ?? "",
        isActive: depollutionStep.isActive,
      });
    } else {
      reset({
        name: "",
        subtitle: "",
        order: defaultOrder,
        needsValue: false,
        options: "",
        isActive: true,
      });
    }
    setSystemField(depollutionStep?.systemField ?? null);
  }, [depollutionStep, open, reset, defaultOrder]);

  return (
    <>
      <ConfirmModal open={deleteOpen} setOpen={setDeleteOpen}
        title={t("Delete Depollution Step")} message={t("Are you sure you want to delete this depollution step? This action cannot be undone.")}
        confirmButtonText={t("Delete")} onConfirm={handleDelete} isLoading={deleteDepollutionStep.isLoading}
      />
      <Modal width="max-w-2xl" open={open} setOpen={setOpen} >
        <h1 className="text-xl">{depollutionStep ? t("Edit a Depollution Step") : t("Add a Depollution Step")}</h1>
        <h1 className="text-sm text-gray-500">{t("All depollution steps will need to be ticked off but can optionally return a value.")}</h1>
        <div className="mt-4">
          <form className="space-y-4 border-t border-gray-200" onSubmit={handleSubmit(onSubmit)} >
            <div className="pt-4 grid grid-cols-1 gap-x-4 gap-y-4 sm:gap-y-6 sm:grid-cols-1">

              <SystemStepFieldPicker
                value={systemField}
                onChange={(field) => {
                  setSystemField(field?.key ?? null);
                  if (field && !watch("name")?.trim()) setValue("name", t(field.label));
                }}
              />

              <TextInput label={t("Name")} registerName="name" register={register} required={true} />
              <TextInput label={t("Subtitle")} registerName="subtitle" register={register} />
              <input type="hidden" {...register("order")} />

              <div className="grid grid-cols-1 gap-x-4 gap-y-4 sm:grid-cols-2">
                {!systemField && (
                  <SwitchInput label={t("Needs Value")} registerName="needsValue" register={register} setValue={setValue} watch={watch} />
                )}
                <SwitchInput label={t("Is Active")} registerName="isActive" register={register} setValue={setValue} watch={watch} />
              </div>

              {!systemField && (
                <>
                  <TextInput
                    label={t("Options")}
                    registerName="options"
                    register={register}
                    placeholder={t("Alloy,Steel,Other")}
                  />
                  <p className="text-sm text-gray-500">
                    {t("Leave options blank for free text, or enter comma-separated values for a dropdown.")}
                  </p>
                </>
              )}

              <ButtonInput
                label={depollutionStep ? t("Save Changes") : t("Create")}
                isSubmit={true}
                onClick={undefined}
                classes={undefined}
                isLoading={createDepollutionStep.isLoading || updateDepollutionStep.isLoading}
              />
              {depollutionStep !== undefined && (
                <ButtonInput label={t("Delete")} isSubmit={false} onClick={() => (setDeleteOpen(true))} classes="!bg-red-500 hover:!bg-red-600" />
              )}
            </div>
          </form>
        </div>
      </Modal>
    </>
  );
}
